import { create } from "@bufbuild/protobuf";
import { type QueryClient, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { GitpodAPI, ServiceAccountHeaders } from "@/api";
import { useEventInterest } from "@/hooks/use-event-interest";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { autoPaginate, autoPaginateRaw } from "@/queries/pagination";
import { keyWithPrincipal, serviceAccountPrincipalKey, type PrincipalKey } from "@/queries/principal-key";
import { useAuthenticatedUser, keys as userQueryKeys, type PlainUser } from "@/queries/user-queries";
import { ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { Principal } from "gitpod-next-api/gitpod/v1/identity_pb";
import {
    CreateEnvironmentClassRequestSchema,
    CreateHostAuthenticationTokenRequestSchema,
    CreateLLMIntegrationRequestSchema,
    CreateSCMIntegrationRequestSchema,
    DeleteLLMIntegrationRequestSchema,
    DeleteSCMIntegrationRequestSchema,
    type EnvironmentClass,
    EnvironmentClassSchema,
    EnvironmentClassValidationResultSchema,
    type FieldValue,
    GetEnvironmentClassRequestSchema,
    GetRunnerConfigurationSchemaRequestSchema,
    type HostAuthenticationToken,
    HostAuthenticationTokenSource,
    ListEnvironmentClassesRequestSchema,
    ListHostAuthenticationTokensRequestSchema,
    ListLLMIntegrationsRequestSchema,
    ListSCMIntegrationsRequestSchema,
    type SCMIntegration,
    SCMIntegrationSchema,
    SCMIntegrationOAuthConfigSchema,
    SCMIntegrationValidationResultSchema,
    UpdateEnvironmentClassRequestSchema,
    UpdateLLMIntegrationRequestSchema,
    UpdateSCMIntegrationRequestSchema,
    ValidateRunnerConfigurationRequestSchema,
    ValidateRunnerConfigurationRequest_ValidateSCMIntegrationSchema,
} from "gitpod-next-api/gitpod/v1/runner_configuration_pb";

export type PlainHostAuthenticationToken = HostAuthenticationToken;
export type PlainSCMIntegration = SCMIntegration;

export const runnerConfigurationQueryKey = {
    list: (principal?: PrincipalKey) => keyWithPrincipal(["hostAuthenticationTokens", "list"], principal),
    listRunnerSCMIntegrations: (runnerId?: string, principal?: PrincipalKey) =>
        keyWithPrincipal(["listRunnerSCMIntegrations", runnerId], principal),
    listAllRunnerSCMIntegrations: (runnerIds: string[]) =>
        keyWithPrincipal(["listAllRunnerSCMIntegrations", runnerIds]),
    listRunnerLLMIntegrations: (runnerId?: string) => keyWithPrincipal(["listRunnerLLMIntegrations", runnerId]),
    listRunnerEnvironmentClasses: (runnerId?: string) => keyWithPrincipal(["listRunnerEnvironmentClasses", runnerId]),
    getEnvironmentClass: (environmentClassId?: string) => keyWithPrincipal(["getEnvironmentClass", environmentClassId]),
    getRunnerConfigurationSchema: (runnerId: string) => keyWithPrincipal(["getRunnerConfigurationSchema", runnerId]),
};

export const handleHostAuthenticationTokenEvent = async (
    _api: GitpodAPI,
    client: QueryClient,
    _evt: WatchEventsResponse,
) => {
    await Promise.all([
        client.invalidateQueries({ queryKey: runnerConfigurationQueryKey.list() }),
        // Also invalidate integration validation queries so the org-level
        // integrations page picks up new OAuth tokens without a page reload.
        client.invalidateQueries({ queryKey: ["integration-validation"] }),
    ]);
};

// Creates a host authentication token for a runner from a personal access token,
// verifies if the token can be used to access a repository, and returns the token.
// If the token does not have the required permissions, it is deleted and an error is thrown.
// If a token already exists for the host, it is deleted and replaced with the new token.
export const useCreateAndVerifyHostAuthenticationTokenPAT = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (options: { pat: string; runnerId: string; scmHost: string; repoUrl: string }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            // First, check if a token already exists for the host, and delete it if it does.
            // We only show the SCM modal if you either don't have a token, or if it's invalid,
            // so removing the old token should be fine.
            const tokens = await autoPaginate(
                (params) => api.runnerConfigurationService.listHostAuthenticationTokens(params),
                create(ListHostAuthenticationTokensRequestSchema, {
                    filter: {
                        userId: user.id,
                        runnerId: options.runnerId,
                    },
                }),
                (response) => response.tokens,
            );
            const existingToken = tokens.find((token) => token.host === options.scmHost);
            if (existingToken) {
                await api.runnerConfigurationService.deleteHostAuthenticationToken({
                    id: existingToken.id,
                });
            }

            const { token } = await api.runnerConfigurationService.createHostAuthenticationToken(
                create(CreateHostAuthenticationTokenRequestSchema, {
                    userId: user.id,
                    runnerId: options.runnerId,
                    host: options.scmHost,
                    token: options.pat,
                    source: HostAuthenticationTokenSource.PAT,
                }),
            );

            // Parse context URL to check if the token is valid and has access to the repo.
            try {
                await api.runnerService.parseContextURL({
                    contextUrl: options.repoUrl,
                    runnerId: options.runnerId,
                });
            } catch (error) {
                // Delete the token if it's invalid.
                if (token) {
                    await api.runnerConfigurationService.deleteHostAuthenticationToken({
                        id: token.id,
                    });
                }

                throw error;
            }

            return token;
        },
        onSuccess: () => {
            client.invalidateQueries({ queryKey: runnerConfigurationQueryKey.list() }).catch(console.error);
        },
    });
};

export type UseCreateHostAuthenticationTokenForServiceAccountParams = {
    headers: ServiceAccountHeaders;
};

// Note(Mads): Might be able to merge with useCreateAndVerifyHostAuthenticationTokenPAT in the future, but for now I decided to keep them separate
// to reduce the blast radius of adding support for service accounts here.
export const useCreateHostAuthenticationTokenForServiceAccount = ({
    headers,
}: UseCreateHostAuthenticationTokenForServiceAccountParams) => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    return useMutation({
        mutationFn: async (options: { host: string; pat: string; runnerId: string }) => {
            const request = create(CreateHostAuthenticationTokenRequestSchema, {
                runnerId: options.runnerId,
                host: options.host,
                token: options.pat,
                source: HostAuthenticationTokenSource.PAT,
                subject: {
                    id: headers.serviceAccountId,
                    principal: Principal.SERVICE_ACCOUNT,
                },
            });
            await api.runnerConfigurationService.createHostAuthenticationToken(request, { headers: headers.headers });
            return;
        },
        onSuccess: () => {
            // We don't handle events for HOST_AUTHENTICATION_TOKEN so we're manually invalidate the list query
            client
                .invalidateQueries({
                    queryKey: runnerConfigurationQueryKey.list(serviceAccountPrincipalKey(headers.serviceAccountId)),
                })
                .catch(console.error);
        },
    });
};

export const useDeleteHostAuthenticationTokenForHost = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (options: { runnerId: string; scmHost: string }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            const tokens = await autoPaginate(
                (params) => api.runnerConfigurationService.listHostAuthenticationTokens(params),
                create(ListHostAuthenticationTokensRequestSchema, {
                    filter: {
                        userId: user.id,
                        runnerId: options.runnerId,
                    },
                }),
                (response) => response.tokens,
            );

            const token = tokens.find((token) => token.host === options.scmHost);
            if (!token) {
                // Already deleted
                return;
            }

            await api.runnerConfigurationService.deleteHostAuthenticationToken({
                id: token.id,
            });

            return;
        },
    });
};

export type DeleteHostAuthenticationTokenParams = {
    headers?: ServiceAccountHeaders;
};

export const useDeleteHostAuthenticationToken = (params?: DeleteHostAuthenticationTokenParams) => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    return useMutation({
        onMutate: async (options: { tokenId: string }) => {
            const queryKey = runnerConfigurationQueryKey.list(
                params?.headers?.serviceAccountId
                    ? serviceAccountPrincipalKey(params.headers.serviceAccountId)
                    : undefined,
            );

            // Cancel in-flight refetches so they don't overwrite the optimistic update
            await client.cancelQueries({ queryKey });

            // Snapshot previous value for rollback
            const previousTokens = client.getQueryData(queryKey);

            // Optimistically remove the token from the cache
            optimisticDeleteHostAuthenticationToken(client, {
                tokenId: options.tokenId,
                headers: params?.headers,
            });

            return { previousTokens, queryKey };
        },
        mutationFn: async (options: { tokenId: string }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }
            await api.runnerConfigurationService.deleteHostAuthenticationToken({
                id: options.tokenId,
            });
        },
        onError: (_error, _options, context) => {
            // Roll back to the previous cache state on failure
            if (context?.previousTokens !== undefined) {
                client.setQueryData(context.queryKey, context.previousTokens);
            }
        },
        onSettled: (_data, _error, _options, context) => {
            // Always refetch after mutation to ensure server state consistency
            if (context?.queryKey) {
                void client.invalidateQueries({ queryKey: context.queryKey });
            }
        },
    });
};

export type UseListHostAuthenticationTokensParams = {
    enabled?: boolean;
    headers?: ServiceAccountHeaders;
};

export const useListHostAuthenticationTokensOptions = ({
    enabled = true,
    headers,
}: UseListHostAuthenticationTokensParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: runnerConfigurationQueryKey.list(
            headers?.serviceAccountId ? serviceAccountPrincipalKey(headers.serviceAccountId) : undefined,
        ),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const tokens = await autoPaginate(
                (params) =>
                    api.runnerConfigurationService.listHostAuthenticationTokens(
                        params,
                        headers ? { headers: headers.headers } : undefined,
                    ),
                create(ListHostAuthenticationTokensRequestSchema, {
                    filter: {
                        subjectId: headers?.serviceAccountId ?? user.id,
                    },
                }),
                (response) => response.tokens,
            );

            return {
                tokens: tokens.map((token) => token),
            };
        },
        enabled: enabled && !!user,
        staleTime: 500,
    });
};

export const useListHostAuthenticationTokens = (params: UseListHostAuthenticationTokensParams) => {
    useEventInterest({ resourceType: ResourceType.HOST_AUTHENTICATION_TOKEN });
    return useQuery(useListHostAuthenticationTokensOptions(params));
};

export const useRunnerConfigurationSchemaOptions = (runnerId: string | undefined) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: runnerConfigurationQueryKey.getRunnerConfigurationSchema(runnerId || "no-runner"),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const { schema } = await api.runnerConfigurationService.getRunnerConfigurationSchema(
                create(GetRunnerConfigurationSchemaRequestSchema, {
                    runnerId: runnerId,
                }),
            );

            return schema;
        },
        enabled: !!user && !!runnerId,
        staleTime: 500,
    });
};

export const useRunnerConfigurationSchema = (runnerId: string | undefined) => {
    return useQuery(useRunnerConfigurationSchemaOptions(runnerId));
};

export type IntegrationConfiguration = {
    scmId: string;
    host?: string;
    pat?: boolean;
    oauth?: {
        clientId: string;
        clientSecret?: string;
        encryptedClientSecret?: Uint8Array;
        issuerUrl?: string;
    };
    virtualDirectory?: string;
};

export const useCreateOrUpdateSCMIntegration = (runnerId: string, integrationId: string | undefined) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (configuration: IntegrationConfiguration) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { result } = await api.runnerConfigurationService.validateRunnerConfiguration(
                create(ValidateRunnerConfigurationRequestSchema, {
                    runnerId,
                    config: {
                        case: "scmIntegration",
                        value: create(ValidateRunnerConfigurationRequest_ValidateSCMIntegrationSchema, {
                            host: configuration.host,
                            pat: configuration.pat,
                            scmId: configuration.scmId,
                            oauthClientId: configuration.oauth?.clientId,
                            oauthClientSecret: configuration.oauth?.clientSecret
                                ? {
                                      case: "oauthPlaintextClientSecret",
                                      value: configuration.oauth?.clientSecret,
                                  }
                                : undefined,
                            issuerUrl: configuration.oauth?.issuerUrl,
                            virtualDirectory: configuration.virtualDirectory,
                        }),
                    },
                }),
            );

            if (result.case === "scmIntegration" && !result.value.valid) {
                return result.value;
            }

            if (!integrationId) {
                const { id: newId } = await api.runnerConfigurationService.createSCMIntegration(
                    create(CreateSCMIntegrationRequestSchema, {
                        runnerId,
                        host: configuration.host,
                        pat: configuration.pat,
                        scmId: configuration.scmId,
                        oauthClientId: configuration.oauth?.clientId,
                        oauthPlaintextClientSecret: configuration.oauth?.clientSecret,
                        issuerUrl: configuration.oauth?.issuerUrl,
                        virtualDirectory: configuration.virtualDirectory,
                    }),
                );
                addSCMIntegrationToCache(
                    client,
                    runnerId,
                    create(SCMIntegrationSchema, {
                        id: newId,
                        ...configuration,
                    }),
                );
                return create(SCMIntegrationValidationResultSchema, { valid: true });
            }

            await api.runnerConfigurationService.updateSCMIntegration(
                create(UpdateSCMIntegrationRequestSchema, {
                    id: integrationId,
                    pat: configuration.pat,
                    oauthClientId: configuration.oauth?.clientId ?? "",
                    oauthPlaintextClientSecret: configuration.oauth?.clientSecret
                        ? configuration.oauth?.clientSecret
                        : undefined,
                    issuerUrl: configuration.oauth?.issuerUrl,
                    virtualDirectory: configuration.virtualDirectory,
                }),
            );

            const scmIntegration = create(SCMIntegrationSchema, {
                id: integrationId,
                runnerId,
                host: configuration.host,
                pat: configuration.pat,
                scmId: configuration.scmId,
                virtualDirectory: configuration.virtualDirectory,
            });

            if (configuration.oauth) {
                scmIntegration.oauth = create(SCMIntegrationOAuthConfigSchema, {
                    clientId: configuration.oauth.clientId,
                    encryptedClientSecret: configuration.oauth.encryptedClientSecret,
                    issuerUrl: configuration.oauth.issuerUrl,
                });
            }

            setSCMIntegrationInCache(client, runnerId, scmIntegration);
            return create(SCMIntegrationValidationResultSchema, { valid: true });
        },
    });
};

export const useValidateEnvironmentClass = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (environmentClass: EnvironmentClass) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { result } = await api.runnerConfigurationService.validateRunnerConfiguration(
                create(ValidateRunnerConfigurationRequestSchema, {
                    runnerId: environmentClass.runnerId,
                    config: {
                        case: "environmentClass",
                        value: environmentClass,
                    },
                }),
            );

            if (result.case === "environmentClass" && !result.value.valid) {
                return result.value;
            }

            return create(EnvironmentClassValidationResultSchema, { valid: true });
        },
    });
};

export const useCreateEnvironmentClass = (runnerId: string) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (values: { displayName: string; description: string; configuration: FieldValue[] }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { id } = await api.runnerConfigurationService.createEnvironmentClass(
                create(CreateEnvironmentClassRequestSchema, {
                    runnerId: runnerId,
                    ...values,
                }),
            );

            addEnvironmentClassToCache(
                client,
                create(EnvironmentClassSchema, {
                    id,
                    runnerId,
                    enabled: true,
                    ...values,
                }),
            );
        },
    });
};

export const useUpdateEnvironmentClass = (environmentClass: EnvironmentClass) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (values: { displayName: string; description: string }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            const updated = create(EnvironmentClassSchema, {
                ...environmentClass,
                displayName: values.displayName,
                description: values.description,
            });

            const { result } = await api.runnerConfigurationService.validateRunnerConfiguration(
                create(ValidateRunnerConfigurationRequestSchema, {
                    runnerId: environmentClass.runnerId,
                    config: {
                        case: "environmentClass",
                        value: updated,
                    },
                }),
            );

            if (result.case === "environmentClass" && !result.value.valid) {
                return result.value;
            }

            await api.runnerConfigurationService.updateEnvironmentClass(
                create(UpdateEnvironmentClassRequestSchema, {
                    environmentClassId: environmentClass.id,
                    displayName: updated.displayName,
                    description: updated.description,
                }),
            );

            setEnvironmentClassInCache(client, updated);
            return create(EnvironmentClassValidationResultSchema, { valid: true });
        },
    });
};

export const useSetEnvironmentClassEnabled = (environmentClass: EnvironmentClass) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (options: { enabled: boolean }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            const updated = create(EnvironmentClassSchema, {
                ...environmentClass,
                enabled: options.enabled,
            });

            await api.runnerConfigurationService.updateEnvironmentClass(
                create(UpdateEnvironmentClassRequestSchema, {
                    environmentClassId: environmentClass.id,
                    displayName: updated.displayName,
                    description: updated.description,
                    enabled: updated.enabled,
                }),
            );

            setEnvironmentClassInCache(client, updated);
        },
    });
};

export const useDeleteSCMIntegration = (runnerId: string) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (integrationId: string) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }
            await api.runnerConfigurationService.deleteSCMIntegration(
                create(DeleteSCMIntegrationRequestSchema, { id: integrationId }),
            );
            removeSCMIntegrationFromCache(client, runnerId, integrationId);
        },
    });
};

export type UseListRunnerSCMIntegrationsParams = {
    enabled?: boolean;
    headers?: ServiceAccountHeaders;
};

export const useListRunnerSCMIntegrationsOptions = (
    runnerId?: string,
    { enabled = true, headers }: UseListRunnerSCMIntegrationsParams = {},
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: runnerConfigurationQueryKey.listRunnerSCMIntegrations(
            runnerId,
            headers ? serviceAccountPrincipalKey(headers?.serviceAccountId) : undefined,
        ),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!runnerId) {
                return [];
            }

            const integrations = await autoPaginate(
                (params) =>
                    api.runnerConfigurationService.listSCMIntegrations(
                        params,
                        headers ? { headers: headers.headers } : undefined,
                    ),
                create(ListSCMIntegrationsRequestSchema, {
                    filter: {
                        runnerIds: [runnerId],
                    },
                }),
                (response) => response.integrations,
            );

            return integrations;
        },
        enabled: !!user && !!runnerId && enabled,
        staleTime: 500,
    });
};

export const useListRunnerSCMIntegrations = (runnerId?: string, params: UseListRunnerSCMIntegrationsParams = {}) => {
    return useQuery(useListRunnerSCMIntegrationsOptions(runnerId, params));
};

export const useListAllRunnerSCMIntegrationsOptions = (runnerIds?: string[]) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: runnerConfigurationQueryKey.listAllRunnerSCMIntegrations(runnerIds ?? []),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const integrations = await autoPaginate(
                (params) => api.runnerConfigurationService.listSCMIntegrations(params),
                create(ListSCMIntegrationsRequestSchema, {
                    // disable filter due to limitation of 25 on the backend
                    // and apply filter in the client
                    // filter: {
                    //     runnerIds,
                    // },
                }),
                (response) => response.integrations,
            );

            if (runnerIds && runnerIds.length > 0) {
                return integrations.filter((integration) => runnerIds.includes(integration.runnerId));
            }

            return integrations;
        },
        enabled: !!user,
        staleTime: 500,
    });
};

export const useListAllRunnerSCMIntegrations = (runnerIds?: string[]) => {
    return useQuery(useListAllRunnerSCMIntegrationsOptions(runnerIds));
};

export const useListRunnerEnvironmentClassesOptions = (runnerId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: runnerConfigurationQueryKey.listRunnerEnvironmentClasses(runnerId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!runnerId) {
                return [];
            }
            const environmentClasses = await autoPaginate(
                (params) => api.runnerConfigurationService.listEnvironmentClasses(params),
                create(ListEnvironmentClassesRequestSchema, { filter: { runnerIds: [runnerId] } }),
                (response) => response.environmentClasses,
            );

            return environmentClasses;
        },
        enabled: !!user && !!runnerId,
        staleTime: 500,
    });
};

export const useListRunnerEnvironmentClasses = (runnerId?: string) => {
    useEventInterest({ resourceType: ResourceType.ENVIRONMENT_CLASS });
    return useQuery(useListRunnerEnvironmentClassesOptions(runnerId));
};

export const useGetEnvironmentClassOptions = (environmentClassId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: runnerConfigurationQueryKey.getEnvironmentClass(environmentClassId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!environmentClassId) {
                return;
            }

            const { environmentClass } = await api.runnerConfigurationService.getEnvironmentClass(
                create(GetEnvironmentClassRequestSchema, {
                    environmentClassId: environmentClassId,
                }),
            );

            return environmentClass;
        },
        enabled: !!user && !!environmentClassId,
        staleTime: 500,
    });
};

export const useGetEnvironmentClass = (environmentClassId?: string) => {
    return useQuery(useGetEnvironmentClassOptions(environmentClassId));
};

function setSCMIntegrationInCache(client: QueryClient, runnerId: string, integration: SCMIntegration) {
    client.setQueryData(runnerConfigurationQueryKey.listRunnerSCMIntegrations(runnerId), (old?: SCMIntegration[]) => {
        let found = false;
        const updated =
            old?.map((i) => {
                if (i.id === integration.id) {
                    found = true;
                    return integration;
                }
                return i;
            }) || [];
        if (!found) {
            updated.push(integration);
        }
        return updated;
    });
}

function removeSCMIntegrationFromCache(client: QueryClient, runnerId: string, integrationId: string) {
    client.setQueryData(
        runnerConfigurationQueryKey.listRunnerSCMIntegrations(runnerId),
        (old?: SCMIntegration[]) => old?.filter((i) => i.id !== integrationId) || [],
    );
}

function addSCMIntegrationToCache(client: QueryClient, runnerId: string, integration: SCMIntegration) {
    client.setQueryData(runnerConfigurationQueryKey.listRunnerSCMIntegrations(runnerId), (old?: SCMIntegration[]) => {
        if (!old) {
            return [integration];
        }
        return [integration, ...old];
    });
}

function setEnvironmentClassInCache(client: QueryClient, environmentClass: EnvironmentClass) {
    client.setQueryData(
        runnerConfigurationQueryKey.listRunnerEnvironmentClasses(environmentClass.runnerId),
        (old: EnvironmentClass[]) => {
            return old.map((ec) => {
                if (ec.id === environmentClass.id) {
                    return environmentClass;
                }
                return ec;
            });
        },
    );
}

function addEnvironmentClassToCache(client: QueryClient, environmentClass: EnvironmentClass) {
    client.setQueryData(
        runnerConfigurationQueryKey.listRunnerEnvironmentClasses(environmentClass.runnerId),
        (old: EnvironmentClass[]) => {
            return [environmentClass, ...old];
        },
    );
}

function optimisticDeleteHostAuthenticationToken(
    client: QueryClient,
    { tokenId, headers }: { tokenId: string; headers?: ServiceAccountHeaders },
) {
    client.setQueryData(
        runnerConfigurationQueryKey.list(
            headers?.serviceAccountId ? serviceAccountPrincipalKey(headers.serviceAccountId) : undefined,
        ),
        (old: { tokens: HostAuthenticationToken[] }) => {
            if (!old) {
                return { tokens: [] };
            }
            return {
                tokens: old.tokens.filter((t) => t.id !== tokenId),
            };
        },
    );
}

// LLM Integration queries
export const useListRunnerLLMIntegrationsOptions = (
    runnerId?: string,
    { enabled = true }: { enabled?: boolean } = {},
) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return queryOptions({
        queryKey: runnerConfigurationQueryKey.listRunnerLLMIntegrations(runnerId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            if (!runnerId) {
                return { integrations: [], llmManagedByOna: false };
            }

            const responses = await autoPaginateRaw(
                (params) => api.runnerConfigurationService.listLLMIntegrations(params),
                create(ListLLMIntegrationsRequestSchema, {
                    filter: {
                        runnerIds: [runnerId],
                    },
                }),
            );

            const integrations = responses.flatMap((response) => response.integrations);
            const llmManagedByOna = responses[0]?.llmManagedByOna || false;

            return {
                integrations,
                llmManagedByOna,
            };
        },
        enabled: !!user && !!runnerId && enabled,
    });
};

export const useListRunnerLLMIntegrations = (runnerId?: string, opts: { enabled?: boolean } = {}) => {
    return useQuery(useListRunnerLLMIntegrationsOptions(runnerId, opts));
};

export const useCreateLLMIntegration = (runnerId: string) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (options: { models: number[]; endpoint: string; apiKey: string; maxTokens?: number }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            const response = await api.runnerConfigurationService.createLLMIntegration(
                create(CreateLLMIntegrationRequestSchema, {
                    runnerId: runnerId,
                    models: options.models,
                    endpoint: options.endpoint,
                    apiKey: options.apiKey,
                    maxTokens: options.maxTokens ? BigInt(options.maxTokens) : undefined,
                }),
            );

            // Invalidate the list to refetch
            await client.invalidateQueries({
                queryKey: runnerConfigurationQueryKey.listRunnerLLMIntegrations(runnerId),
            });

            return response;
        },
    });
};

export const useUpdateLLMIntegration = (runnerId: string) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async (options: {
            integrationId: string;
            models?: number[];
            endpoint?: string;
            apiKey?: string;
            maxTokens?: number;
            phase?: number;
        }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }

            await api.runnerConfigurationService.updateLLMIntegration(
                create(UpdateLLMIntegrationRequestSchema, {
                    id: options.integrationId,
                    models: options.models,
                    endpoint: options.endpoint,
                    apiKey: options.apiKey,
                    maxTokens: options.maxTokens ? BigInt(options.maxTokens) : undefined,
                    phase: options.phase,
                }),
            );

            // Invalidate the list to refetch
            await client.invalidateQueries({
                queryKey: runnerConfigurationQueryKey.listRunnerLLMIntegrations(runnerId),
            });
        },
    });
};

export const useDeleteLLMIntegration = (runnerId: string) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ integrationId, force = false }: { integrationId: string; force?: boolean }) => {
            const user = client.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            if (!user) {
                throw new Error("User not authenticated");
            }
            await api.runnerConfigurationService.deleteLLMIntegration(
                create(DeleteLLMIntegrationRequestSchema, { id: integrationId, force }),
            );

            // Invalidate the list to refetch
            await client.invalidateQueries({
                queryKey: runnerConfigurationQueryKey.listRunnerLLMIntegrations(runnerId),
            });
        },
    });
};
